import { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Form';

/**
 * Simple yes/no confirmation. For dialogs with form inputs, use ConfirmActionModal.
 * Props: onConfirm fires on accept, onCancel fires on dismiss.
 *
 * Async support — `onConfirm` may return a Promise. If it rejects, the error
 * message is surfaced inline below the message body (mirrors the PWA
 * ConfirmDialog MED-07 fix). Existing sync callers continue to work because
 * `await` on a non-promise is a no-op.
 */
interface Props {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = '확인',
  cancelLabel = '취소',
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}: Props) {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  async function handleConfirm() {
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류가 발생했습니다.');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      width={420}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={busy}
            data-testid="confirm-dialog-cancel"
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={danger ? 'danger' : 'primary'}
            onClick={handleConfirm}
            disabled={busy}
            data-testid="confirm-dialog-confirm"
          >
            {busy ? '처리 중…' : confirmLabel}
          </Button>
        </>
      }
    >
      <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', color: 'var(--fg-default)' }}>
        {message}
      </div>
      {error && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: '8px 10px',
            background: 'var(--bg-danger-subtle)',
            border: '1px solid var(--fg-danger)',
            color: 'var(--fg-danger)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-sm)',
          }}
        >
          {error}
        </div>
      )}
    </Modal>
  );
}
